import { createFileRoute } from "@tanstack/react-router";
import { SEOPage } from "@/components/site/SEOPage";
import { Server, ShieldCheck, Zap, TrendingUp, CheckCircle2 } from "lucide-react";
import { motion } from "framer-motion";

export const Route = createFileRoute("/painel-iptv/franquia")({
  head: () => ({
    meta: [
      { title: "Franquia IPTV Pós Pago - Servidores P2PRO e Prime Play | P2 PRO" },
      { name: "description", content: "Abra sua Franquia IPTV com os melhores servidores do mercado: P2PRO e Prime Play. Sistema pós-pago, alta lucratividade e suporte 24/7. Comece hoje!" },
      { name: "keywords", content: "franquia iptv, franquia iptv pos pago, servidor p2pro franquia, servidor prime play franquia, abrir franquia de streaming" },
    ],
  }),
  component: () => (
    <SEOPage
      variant="split"
      showTestimonials={true}
      hideStats={false}
      showPriceTable={false}
      category="Oportunidade de Negócio"
      title="FRANQUIA IPTV PÓS PAGO: P2PRO & PRIME PLAY"
      subtitle="Domine o mercado de streaming com a estrutura mais sólida do Brasil. Tenha seu próprio servidor master com tecnologia de ponta."
      heroImage="https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&q=80&w=1200"
      introParagraphs={[
        "A Franquia IPTV da P2 PRO representa o ápice do empreendedorismo no mercado de streaming. Ao contrário da revenda tradicional, como franqueado você opera com a <a href='/' className='text-electric hover:underline font-bold'>infraestrutura definitiva de Painel IPTV Pós Pago</a>, garantindo autoridade máxima perante seus clientes e revendedores.",
        "Nossa estrutura master permite que você gerencie sua própria rede com autonomia total, utilizando o sistema Pós-Pago que elimina a necessidade de estoques de créditos e permite um fluxo de caixa extremamente saudável para o seu crescimento.",
        "Seja com a ultra-estabilidade do P2PRO ou com o custo-benefício imbatível do Prime Play, sua franquia estará equipada com o que há de mais moderno em CDN global e tecnologia anti-bloqueio em 2026."
      ]}
      benefits={[
        {
          title: "Servidores de Elite",
          description: "Acesso exclusivo aos servidores P2PRO e Prime Play, referências mundiais em uptime e qualidade 4K.",
          icon: <Server className="w-6 h-6" />
        },
        {
          title: "Modelo Pós-Pago",
          description: "Escalabilidade real sem investimento prévio em créditos. Você paga apenas pelo que seus clientes utilizam.",
          icon: <TrendingUp className="w-6 h-6" />
        },
        {
          title: "Suporte Master 24/7",
          description: "Equipe de engenharia dedicada para garantir que sua operação master nunca pare, nem nos horários de pico.",
          icon: <ShieldCheck className="w-6 h-6" />
        }
      ]}
      howItWorks={[
        { title: "Seleção de Servidor", description: "Escolha entre P2PRO, Prime Play ou ambos para compor seu portfólio master." },
        { title: "Ativação Master", description: "Configuramos seu painel de franqueado com todas as ferramentas de gestão e automação." },
        { title: "Treinamento", description: "Você recebe o manual de operações para gerenciar revendedores e escalar sua marca." },
        { title: "Escala", description: "Comece a faturar com recorrência utilizando a melhor infraestrutura do mercado." }
      ]}
      diferenciais={[
        { title: "Anti-Bloqueio Ativo", description: "Tecnologia exclusiva que protege sua rede contra instabilidades regionais." },
        { title: "VOD Gigante", description: "Mais de 120.000 títulos atualizados diariamente em ambos os servidores." },
        { title: "Painel Intuitivo", description: "Gestão simplificada de toda a sua rede de revendedores em um único local." }
      ]}
      sections={[
        {
          title: "Nossos Servidores de Franquia",
          type: "grid",
          content: (
            <>
              <div className="p-8 rounded-[2.5rem] bg-gradient-to-br from-electric/20 to-transparent border border-electric/30 relative overflow-hidden group hover:scale-[1.02] transition-all duration-500">
                <div className="absolute top-0 right-0 p-4 opacity-10">
                  <Zap className="w-24 h-24 text-electric" />
                </div>
                <h3 className="text-3xl font-black italic uppercase mb-4 text-electric">P2PRO MASTER</h3>
                <p className="text-muted-foreground mb-6 leading-relaxed">O servidor flagship para quem não aceita menos que a perfeição. Ideal para clientes corporativos e revendas de alto ticket.</p>
                <ul className="space-y-3">
                  {["Canais 4K Nativo", "Latência Zero", "App Exclusivo P2PRO", "Foco em Estabilidade"].map(item => (
                    <li key={item} className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-foreground/80">
                      <CheckCircle2 className="w-4 h-4 text-electric" /> {item}
                    </li>
                  ))}
                </ul>
              </div>

              <div className="p-8 rounded-[2.5rem] bg-white/[0.03] border border-white/10 relative overflow-hidden group hover:scale-[1.02] transition-all duration-500">
                <div className="absolute top-0 right-0 p-4 opacity-10">
                  <Server className="w-24 h-24 text-white" />
                </div>
                <h3 className="text-3xl font-black italic uppercase mb-4">PRIME PLAY</h3>
                <p className="text-muted-foreground mb-6 leading-relaxed">O equilíbrio perfeito entre preço e performance. A escolha número 1 para expansão massiva de base de usuários.</p>
                <ul className="space-y-3">
                  {["Grade Completa VOD", "Interface Moderna", "Ativação Instantânea", "Alta Rentabilidade"].map(item => (
                    <li key={item} className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-foreground/80">
                      <CheckCircle2 className="w-4 h-4 text-electric" /> {item}
                    </li>
                  ))}
                </ul>
              </div>

              <div className="p-8 rounded-[2.5rem] bg-electric/10 border border-electric/20 flex flex-col justify-center items-center text-center">
                <TrendingUp className="w-12 h-12 text-electric mb-4" />
                <h3 className="text-xl font-black uppercase mb-2">Potencial de Lucro</h3>
                <p className="text-sm text-muted-foreground italic">Franqueados P2 PRO atingem o ROI (Retorno sobre Investimento) em tempo recorde devido ao modelo de escala infinita.</p>
              </div>
            </>
          )
        },
        {
          title: "Por Que Escolher Nossa Franquia?",
          type: "default",
          content: (
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div className="space-y-6">
                <p className="text-lg text-muted-foreground leading-relaxed">
                  Trabalhamos com uma infraestrutura de <strong>CDN (Content Delivery Network)</strong> global, garantindo que o sinal chegue ao seu cliente com a menor rota possível. Isso elimina o buffering e garante a satisfação total.
                </p>
                <div className="p-6 rounded-2xl bg-white/[0.02] border border-white/10 italic text-muted-foreground border-l-4 border-l-electric">
                  "A Franquia P2 PRO mudou meu patamar de faturamento. Hoje gerencio mais de 50 revendedores com total estabilidade nos servidores P2PRO e Prime Play."
                </div>
              </div>
              <motion.div 
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                className="relative aspect-video rounded-3xl overflow-hidden border border-white/10"
              >
                <img src="https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&q=80&w=800" alt="Dashboard Master IPTV" className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-background via-transparent to-transparent" />
              </motion.div>
            </div>
          )
        }
      ]}
      faq={[
        {
          question: "Como funciona o sistema pós-pago na franquia?",
          answer: "Diferente de comprar créditos antecipados, na franquia você ativa os clientes e paga apenas o valor de custo por usuário ativo no final do período, garantindo que você nunca tenha dinheiro 'parado' em estoque."
        },
        {
          question: "Posso oferecer os dois servidores aos meus revendedores?",
          answer: "Sim! Como franqueado master, você tem flexibilidade total para oferecer tanto o P2PRO quanto o Prime Play em seu portfólio."
        },
        {
          question: "Qual o suporte técnico oferecido?",
          answer: "Você tem canal direto com nossa engenharia 24/7. Qualquer atualização ou manutenção é gerenciada por nós, garantindo que sua operação seja 100% transparente para o usuário final."
        }
      ]}
      breadcrumbs={[
        { label: "Painel IPTV", href: "/painel-iptv" },
        { label: "Franquia", href: "/painel-iptv/franquia" }
      ]}
      ctaTitle="🚀 Torne-se um Franqueado Master P2 PRO"
      ctaDescription="Libere agora sua estrutura मास्टर com os servidores P2PRO e Prime Play. Vagas limitadas por região."
      ctaButtonText="QUERO MINHA FRANQUIA AGORA"
    />
  ),
});
